import type { FormConfig } from "@amzn/aquila-feedback-web-assets";
import type { TemplateAssociations } from "@amzn/d16-g-cdb-publisher-portal-typescript-client";
import {
    useAsync,
    useMountEffect,
    type AsyncStatus,
    type UseAsyncActions,
} from "@react-hookz/web";
import { cloneDeep, set } from "lodash";
import { useState } from "react";
import { useStore } from "zustand";

import { getPublisherPortalApiClient } from "../../../../api/publisherPortalApi/publisherPortalApi";
import { reportIssueStore } from "../../../../store/reportIssue";

import { useGetReportIssueForm } from "./useGetReportIssueForm";

interface GetReportIssueFormProps {
    reportIssueFormConfig: { configId: string; awsAccountId: string };
}

export const useGetAllInsghtsTabReportIssueForm = ({
    reportIssueFormConfig,
}: GetReportIssueFormProps): [
    FormConfig,
    AsyncStatus,
    UseAsyncActions<void, []>,
    (fieldName: string, value: string) => void,
] => {
    const [_, formConfigAsyncStatus, getFormConfigAsyncAction] =
        useGetReportIssueForm({
            reportIssueFormConfig,
        });
    const [formConfigMap, setFormConfig] = useStore(
        reportIssueStore,
        (state) => [state.formConfigMap, state.setFormConfig],
    );
    const [templates, setTemplates] = useState<TemplateAssociations[]>([]);

    const [, onDemandInsightsAction] = useAsync(async () => {
        const { data = [] } = await (
            await getPublisherPortalApiClient()
        ).getTemplatesWithAssociations({});
        return setTemplates(data);
    });

    useMountEffect(() => {
        void onDemandInsightsAction.execute();
    });

    // No longer adding Request Template Access functionality

    const updateFormConfig = (fieldName: string, value: string) => {
        const previsousConfig = cloneDeep(
            reportIssueStore.getState().formConfigMap[
                reportIssueFormConfig.configId
            ] ?? {},
        );
        if (fieldName === "packagedInsightType") {
            const filteredTemplates = templates.filter((template) =>
                template.domain?.tags?.includes?.(value),
            );
            const templateItems = filteredTemplates.map((template) => ({
                label: template.name,
                value: template.domain?.id,
            }));
            set(
                previsousConfig,
                "body.0.elements.4.props.items",
                templateItems ?? [],
            );
            setFormConfig(reportIssueFormConfig.configId, previsousConfig);
        }
    };

    return [
        formConfigMap[reportIssueFormConfig.configId],
        formConfigAsyncStatus,
        getFormConfigAsyncAction,
        updateFormConfig,
    ];
};
